import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { anlagenAPI, kundenAPI } from '../services/api';
import { Plus, Search, Settings, Filter } from 'lucide-react';
import Navbar from '../components/Navbar';

const Anlagen = () => {
  const [anlagen, setAnlagen] = useState([]);
  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKunde, setSelectedKunde] = useState('');
  const [lecFilter, setLecFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [anlagenData, kundenData] = await Promise.all([
        anlagenAPI.getAll(),
        kundenAPI.getAll()
      ]);
      setAnlagen(anlagenData);
      setKunden(kundenData);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (anlageId, anlageName) => {
    if (window.confirm(`Möchten Sie die Anlage "${anlageName}" wirklich löschen?`)) {
      try {
        await anlagenAPI.delete(anlageId);
        setAnlagen(anlagen.filter(a => a.id !== anlageId));
        alert('Anlage erfolgreich gelöscht');
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
        alert('Fehler beim Löschen der Anlage');
      }
    }
  };

  const getKundeName = (customerId) => {
    const kunde = kunden.find(k => k.id === customerId);
    return kunde ? kunde.firma : 'Unbekannt';
  };

  const filteredAnlagen = anlagen.filter(anlage => {
    const matchesSearch = 
      (anlage.anlagenbezeichnung && anlage.anlagenbezeichnung.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (anlage.hersteller && anlage.hersteller.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (anlage.modell && anlage.modell.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (anlage.seriennummer && anlage.seriennummer.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesKunde = !selectedKunde || anlage.customer_id === selectedKunde;
    
    const matchesLec = !lecFilter || 
      (anlage.lec_anlagennummer && anlage.lec_anlagennummer.includes(lecFilter));
    
    return matchesSearch && matchesKunde && matchesLec;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Anlagenverwaltung</h1>
          <div className="flex gap-3">
            <Link
              to="/import"
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition"
            >
              <Plus className="w-5 h-5" />
              Import aus VDKF-LEC
            </Link>
            <Link
              to="/anlage/neu"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition"
            >
              <Plus className="w-5 h-5" />
              Neue Anlage
            </Link>
          </div>
        </div>

        {/* Suchleiste und Filter */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Allgemeine Suche */}
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Suche
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Bezeichnung, Hersteller, Modell..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Kunden-Filter */}
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nach Kunde filtern
              </label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={selectedKunde}
                  onChange={(e) => setSelectedKunde(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                >
                  <option value="">Alle Kunden</option>
                  {kunden.map(kunde => (
                    <option key={kunde.id} value={kunde.id}>
                      {kunde.firma}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* LEC-Nummer Filter */}
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                LEC-Anl.-Nr.
              </label>
              <input
                type="text"
                placeholder="LEC-Nummer eingeben..."
                value={lecFilter}
                onChange={(e) => setLecFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Reset Filter Button */}
          {(searchTerm || selectedKunde || lecFilter) && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedKunde('');
                  setLecFilter('');
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Alle Filter zurücksetzen
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Laden...</div>
          </div>
        ) : filteredAnlagen.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Keine Anlagen gefunden</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || selectedKunde || lecFilter
                ? 'Versuchen Sie andere Filter'
                : 'Erstellen Sie Ihre erste Anlage oder importieren Sie Daten aus VDKF-LEC'}
            </p>
            {!searchTerm && !selectedKunde && !lecFilter && (
              <div className="flex gap-3 justify-center">
                <Link
                  to="/import"
                  className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition"
                >
                  <Plus className="w-5 h-5" />
                  Daten importieren
                </Link>
                <Link
                  to="/anlage/neu"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition"
                >
                  <Plus className="w-5 h-5" />
                  Neue Anlage
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      LEC-Anl.-Nr.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bezeichnung
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kunde
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Typ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hersteller
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Standort
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAnlagen.map((anlage) => (
                    <tr 
                      key={anlage.id}
                      className="hover:bg-gray-50 cursor-pointer transition"
                      onClick={() => navigate(`/anlage/${anlage.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {anlage.lec_anlagennummer || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {anlage.anlagenbezeichnung || 'Ohne Bezeichnung'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {anlage.modell}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getKundeName(anlage.customer_id)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {anlage.anlagentyp}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {anlage.hersteller}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div>{anlage.standort_gebaeude}</div>
                        <div className="text-xs text-gray-400">{anlage.standort_raum}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/anlage/${anlage.id}`);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Details
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(anlage.id, anlage.anlagenbezeichnung || 'Anlage');
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Löschen
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && filteredAnlagen.length > 0 && (
          <div className="mt-4 text-sm text-gray-600 text-center">
            {filteredAnlagen.length} von {anlagen.length} Anlagen
          </div>
        )}
      </div>
    </div>
  );
};

export default Anlagen;
